import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Mail, Lock, ArrowLeft, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import GoogleAuth from '../components/GoogleAuth';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_delivery-desk-12/artifacts/76spvmi3_3D4A54DE-AC29-47B8-ABEA-5150F325A161.png';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const next = new URLSearchParams(search).get('next');
  const destino = next && next.startsWith('/') && !next.startsWith('//') ? next : '/dashboard';
  const { toast } = useToast();
  const { login } = useAuth();

  useEffect(() => {
    try {
      const aviso = sessionStorage.getItem('inn_aviso');
      if (aviso) { sessionStorage.removeItem('inn_aviso'); toast({ title: 'Sessão encerrada', description: aviso }); }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast({ title: 'Preencha todos os campos' });
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast({ title: 'Login realizado!', description: 'Bem-vindo de volta à In\'Nova Envios.' });
      navigate(destino);
    } catch (err) {
      toast({ title: 'Erro ao entrar', description: err?.response?.data?.detail || 'Tente novamente.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_440px] xl:grid-cols-[minmax(0,1fr)_460px] lg:gap-5 lg:p-5 bg-white lg:bg-[#EEF2F7]">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-[#1E429F] via-[#2A5BC7] to-[#3970E0] text-white relative overflow-hidden lg:rounded-[28px] lg:shadow-[0_12px_40px_rgba(21,55,138,0.18)]">
        {/* foto de fundo (caixas e envelopes voando) + véu azul à esquerda para o texto ficar legível */}
        <img src="/img/login-envios.webp" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-[62%_center]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#15378A]/95 via-[#1E429F]/70 to-[#1E429F]/5" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#15378A]/90 to-transparent" />
        <Link to="/" className="relative z-10"><img src={LOGO_URL} alt="In'Nova Envios" className="h-36" /></Link>
        <div className="relative z-10">
          <h2 className="text-4xl font-extrabold leading-tight">O jeito inteligente de enviar.</h2>
          <p className="mt-4 text-white/85 text-lg max-w-md">Acesse sua conta e continue economizando nos seus fretes dos Correios para todo o Brasil.</p>
          <ul className="mt-8 space-y-3 text-white/90">
            <li className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-[#FFD43B]" /> Carteira pré-paga na plataforma</li>
            <li className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-[#FFD43B]" /> Correios direto da plataforma</li>
            <li className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-[#FFD43B]" /> Suporte humanizado</li>
          </ul>
        </div>
        <p className="relative z-10 text-sm text-white/60">{new Date().getFullYear()} © In&apos;Nova Envios — Desenvolvido por <strong className="text-white/80">In&apos;Nova Art and Sciences</strong> | Todos os direitos reservados</p>
      </div>

      <div className="flex flex-col p-6 sm:p-10 lg:p-10 bg-white lg:rounded-[28px] lg:border lg:border-slate-200/70 lg:shadow-[0_8px_30px_rgba(16,25,46,0.06)]">
        <Link to="/" className="flex items-center text-sm text-slate-500 hover:text-[#2A5BC7] mb-8">
          <ArrowLeft className="w-4 h-4 mr-1" /> Voltar para o site
        </Link>
        <div className="lg:hidden mb-8"><img src={LOGO_URL} alt="In'Nova Envios" className="h-16" /></div>

        <div className="max-w-md w-full mx-auto lg:mx-0 flex-1 flex flex-col justify-center">
          <h1 className="text-3xl font-extrabold text-slate-900">Entrar na sua conta</h1>
          <p className="mt-2 text-slate-600">Bem-vindo de volta! Insira seus dados para continuar.</p>

          <form onSubmit={handleLogin} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email">E-mail</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="email" type="email" placeholder="voce@email.com" value={email} onChange={(e)=>setEmail(e.target.value)} className="pl-9 h-11" />
              </div>
            </div>
            <div>
              <Label htmlFor="password">Senha</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="password" type={showPwd ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={(e)=>setPassword(e.target.value)} className="pl-9 pr-10 h-11" />
                <button type="button" onClick={()=>setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-slate-600 cursor-pointer">
                <input type="checkbox" className="rounded border-slate-300" /> Lembrar de mim
              </label>
              <Link to="/esqueci-senha" className="text-[#2A5BC7] font-semibold hover:text-[#F77820]">Esqueci a senha</Link>
            </div>
            <Button type="submit" disabled={loading} className="w-full inn-btn-primary h-12 rounded-full">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Entrando...</> : 'Entrar'}
            </Button>
          </form>

          <GoogleAuth onSuccess={() => navigate(destino)} />

          <p className="text-center text-sm text-slate-600 mt-6">
            Ainda não tem conta? <Link to={`/cadastro${search}`} className="font-semibold text-[#2A5BC7] hover:text-[#F77820]">Crie agora grátis</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
